import { useMemo, useState } from "react";
import { Users, Search, Pencil } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { ContactLinks } from "@/components/contacts/ContactLinks";
import { ContactsDialog } from "@/components/travel/ContactsDialog";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Person = ({ c, w, b, index, i }) => (
  <li className="flex items-center gap-3 py-2.5" data-testid={`people-contact-${slug(w.country)}-${i}`}>
    <span className="w-8 h-8 rounded-full bg-amber-400/10 text-amber-200 grid place-items-center text-xs font-semibold shrink-0">{c.name[0]?.toUpperCase()}</span>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium truncate">{c.name}</p>
      {c.notes && <p className="text-xs text-slate-500 truncate">{c.notes}</p>}
    </div>
    <ContactLinks c={c} testId={`people-links-${slug(w.country)}-${i}`} />
    <ContactsDialog w={w} b={b} index={index} editAt={i}>
      <button className="p-1.5 text-slate-500 hover:text-amber-300" aria-label={`Edit ${c.name}`} data-testid={`people-edit-${slug(w.country)}-${i}`}><Pencil size={14} /></button>
    </ContactsDialog>
  </li>
);

export const PeopleSheet = ({ wishlist, b }) => {
  const [q, setQ] = useState("");
  const groups = useMemo(() => {
    const t = q.trim().toLowerCase();
    return wishlist
      .map((w, idx) => ({ w, idx, people: w.contacts.map((c, i) => ({ c, i })).filter(({ c }) => !t || w.country.toLowerCase().includes(t) || `${c.name} ${c.notes}`.toLowerCase().includes(t)) }))
      .filter((g) => g.people.length);
  }, [wishlist, q]);
  const total = wishlist.reduce((n, w) => n + w.contacts.length, 0);
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button className="flex items-center gap-1.5 text-xs rounded-full border border-white/15 text-slate-300 px-3 py-1.5 hover:border-amber-400/50 hover:text-amber-200 transition-colors" data-testid="people-open-button">
          <Users size={13} /> My people{total ? ` · ${total}` : ""}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#0E131D] border-white/10 overflow-y-auto" data-testid="people-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">My people</SheetTitle>
          <SheetDescription>{total} contact{total === 1 ? "" : "s"} across {wishlist.filter((w) => w.contacts.length).length} countries. Only you can see these.</SheetDescription>
        </SheetHeader>
        <label className="mt-5 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
          <Search size={14} className="text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search names or countries…" className="bg-transparent outline-none text-sm flex-1" data-testid="people-search-input" />
        </label>
        <div className="mt-4 space-y-5">
          {groups.map(({ w, idx, people }) => (
            <section key={w.id} data-testid={`people-group-${slug(w.country)}`}>
              <h3 className="flex items-baseline gap-2 text-xs uppercase tracking-wider text-amber-300/90 border-b border-white/5 pb-1.5">
                <span className="font-mono text-slate-500">#{String(idx + 1).padStart(2, "0")}</span> {w.country}
                <span className="ml-auto text-slate-500 normal-case tracking-normal">{w.contacts.length}</span>
              </h3>
              <ul className="divide-y divide-white/5">
                {people.map(({ c, i }) => <Person key={i} c={c} w={w} b={b} index={idx + 1} i={i} />)}
              </ul>
            </section>
          ))}
          {!groups.length && <p className="text-sm text-slate-500" data-testid="people-empty">{total ? "No one matches that search." : "No contacts yet. Add them from the contacts button on any Want to go country."}</p>}
        </div>
      </SheetContent>
    </Sheet>
  );
};
